import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOverallReport } from './useOverallReport';
import { useInvoiceStats } from './useInvoices';
import { useCompanyExpenses, EXPENSE_FREQUENCIES } from './useCompanyExpenses';
import { useVisibility } from './useVisibility';
import { OutstandingJob, AgingBucket, FinanceIncomeSummary, PaymentMethodTotals, ExpenseCategorySummary } from '@/types/finance';
import { DEFAULT_TIMEZONE, formatDateInputInTz } from '@/utils/timezone';
import { differenceInDays, parseISO } from 'date-fns';

export const useFinanceReport = (startDate: Date, endDate: Date, timezone: string = DEFAULT_TIMEZONE) => {
  // Reuse existing hooks for core metrics
  const overallReport = useOverallReport(startDate, endDate, timezone);
  const invoiceStats = useInvoiceStats(startDate, endDate, timezone);
  const {
    activeExpenses,
    isLoading: expensesLoading,
    filterExpensesByPeriod,
    calculatePeriodAmount,
  } = useCompanyExpenses();
  const { canSeeCompanyFinancials } = useVisibility();

  // Fetch outstanding jobs (completed but not fully paid)
  const outstandingJobsQuery = useQuery({
    queryKey: ['reports', 'outstanding-jobs', startDate, endDate, timezone],
    enabled: canSeeCompanyFinancials,
    queryFn: async (): Promise<OutstandingJob[]> => {
      const startDateStr = formatDateInputInTz(startDate, timezone);
      const endDateStr = formatDateInputInTz(endDate, timezone);

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          job_number,
          customer_name,
          customer_id,
          job_date,
          invoice_total,
          amount_paid,
          amount_outstanding,
          payment_status
        `)
        .eq('status', 'completed')
        .in('payment_status', ['unpaid', 'partially_paid'])
        .gte('job_date', startDateStr)
        .lte('job_date', endDateStr)
        .order('job_date', { ascending: true });

      if (error) throw error;

      const today = new Date();

      return (data || []).map(job => ({
        id: job.id,
        job_number: job.job_number,
        customer_name: job.customer_name,
        customer_id: job.customer_id,
        job_date: job.job_date || '',
        days_outstanding: job.job_date
          ? differenceInDays(today, parseISO(job.job_date))
          : 0,
        invoice_total: job.invoice_total || 0,
        amount_paid: job.amount_paid || 0,
        outstanding_amount: job.amount_outstanding || 0,
        payment_status: job.payment_status as 'unpaid' | 'partially_paid',
      }));
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  // Calculate income summary from overall report data
  const incomeSummary: FinanceIncomeSummary | null = overallReport.data ? {
    // totalRevenue = the full amount billed (all invoiced jobs), regardless of payment
    totalRevenue: overallReport.data.totalInvoiced,
    // gstCollected = GST component of money actually received
    gstCollected: overallReport.data.gstOwed,
    // netRevenue = collected revenue after removing GST
    netRevenue: overallReport.data.turnoverAfterTax,
    // totalCollected = real cash received (cash + card + bank + other)
    // This is identical to totalTurnover now that WFP placeholders are gone
    totalCollected: overallReport.data.totalTurnover,
    // outstandingBalance = total billed minus total collected
    // This is the true accounts-receivable figure for the period
    outstandingBalance: overallReport.data.totalInvoiced - overallReport.data.totalTurnover,
  } : null;

  // Payment method totals
  const paymentMethods: PaymentMethodTotals | null = overallReport.data
    ? overallReport.data.paymentMethodBreakdown
    : null;

  // Calculate aging buckets from outstanding jobs
  const agingBuckets: AgingBucket[] = (() => {
    const jobs = outstandingJobsQuery.data || [];
    const buckets: AgingBucket[] = [
      { label: 'Current (0-30)', min: 0, max: 30, count: 0, total: 0 },
      { label: '31-60 Days', min: 31, max: 60, count: 0, total: 0 },
      { label: '61-90 Days', min: 61, max: 90, count: 0, total: 0 },
      { label: '90+ Days', min: 91, max: null, count: 0, total: 0 },
    ];

    jobs.forEach(job => {
      const days = job.days_outstanding;
      const bucket = buckets.find(b =>
        days >= b.min && (b.max === null || days <= b.max)
      );
      if (bucket) {
        bucket.count++;
        bucket.total += job.outstanding_amount;
      }
    });

    return buckets;
  })();

  // Filter expenses by the selected period
  const filteredExpenses = filterExpensesByPeriod(activeExpenses, startDate, endDate);

  // Calculate expense category totals (includes one-time at full value)
  const expenseCategorySummary: ExpenseCategorySummary[] = (() => {
    const categoryMap = new Map<string, { label: string; total: number }>();

    filteredExpenses.forEach(expense => {
      const existing = categoryMap.get(expense.category);
      const periodAmount = calculatePeriodAmount(expense);

      if (existing) {
        existing.total += periodAmount;
      } else {
        categoryMap.set(expense.category, {
          label: expense.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          total: periodAmount,
        });
      }
    });

    return Array.from(categoryMap.entries()).map(([category, data]) => ({
      category,
      ...data,
    })).sort((a, b) => b.total - a.total);
  })();

  const totalExpenses = expenseCategorySummary.reduce((sum, cat) => sum + cat.total, 0);

  // Total outstanding from outstanding jobs
  const totalOutstanding = (outstandingJobsQuery.data || [])
    .reduce((sum, job) => sum + job.outstanding_amount, 0);

  return {
    // Data
    incomeSummary,
    paymentMethods,
    outstandingJobs: outstandingJobsQuery.data || [],
    agingBuckets,
    invoiceStats: invoiceStats.data,
    overallMetrics: overallReport.data,
    expenseCategorySummary,
    totalExpenses,
    totalOutstanding,

    // Loading states
    isLoading: overallReport.isLoading || outstandingJobsQuery.isLoading || expensesLoading,
    isInvoiceStatsLoading: invoiceStats.isLoading,

    // Errors
    error: overallReport.error || outstandingJobsQuery.error,
  };
};
// Fetch jobs for determines payment method details
export const usePaymentMethodJobs = (
  startDate: Date,
  endDate: Date,
  timezone: string,
  paymentMethod: string | null
) => {
  const { canSeeCompanyFinancials } = useVisibility();
  return useQuery({
    queryKey: ['reports', 'payment-method-jobs', startDate, endDate, timezone, paymentMethod],
    enabled: !!paymentMethod && canSeeCompanyFinancials,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    queryFn: async () => {
      if (!paymentMethod) return [];

      const startDateStr = formatDateInputInTz(startDate, timezone);
      const endDateStr = formatDateInputInTz(endDate, timezone);

      const { data, error } = await supabase
        .from('job_payments')
        .select(`
          id,
          amount,
          payment_date,
          payment_method,
          job:jobs!inner (
            id,
            job_number,
            customer_name,
            job_date
          )
        `)
        .eq('payment_method', paymentMethod)
        .gte('jobs.job_date', startDateStr)
        .lte('jobs.job_date', endDateStr)
        .order('payment_date', { ascending: false });

      if (error) {
        console.error('Error fetching payment details:', error);
        throw error;
      }

      return data.map(payment => ({
        id: payment.id,
        jobId: payment.job.id,
        jobNumber: payment.job.job_number,
        customerName: payment.job.customer_name,
        date: payment.payment_date,
        amount: payment.amount,
        closeDate: payment.job.job_date
      }));
    }
  });
};
